import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { surveyApi } from '../../services/api';
import {
  StatCard, EmptyState, Spinner, ConfirmModal,
} from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  UsersIcon, UserGroupIcon, CheckBadgeIcon, SparklesIcon, FaceSmileIcon,
  ArrowDownTrayIcon, LockClosedIcon,
} from '@heroicons/react/24/outline';

function scoreColor(score) {
  if (score == null) return 'bg-gray-100 text-gray-400';
  if (score >= 4.5)  return 'bg-green-500 text-white';
  if (score >= 3.5)  return 'bg-green-200 text-green-900';
  if (score >= 2.5)  return 'bg-amber-200 text-amber-900';
  return 'bg-red-400 text-white';
}

function sentimentChip(v) {
  if (v == null) return <span className="text-xs text-gray-400">—</span>;
  const n = Number(v);
  const cls =
    n >= 0.3  ? 'bg-green-100 text-green-800' :
    n <= -0.3 ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-700';
  const label =
    n >= 0.3  ? 'Positive' :
    n <= -0.3 ? 'Negative' :
                'Neutral';
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', cls)}>
      {label} ({n.toFixed(2)})
    </span>
  );
}

function csvEscape(v) {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const lines = [
    headers.join(','),
    ...rows.map(r => headers.map(h => csvEscape(r[h])).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function OverviewTab({ results, heatmap }) {
  const {
    survey, response_count, target_count, completion_rate,
    avg_score, avg_sentiment, questions = [],
  } = results;

  // Derive per-question averages: prefer results.questions; fallback to heatmap
  const questionAverages = useMemo(() => {
    if (questions.length) {
      return questions.map(q => ({
        id: q.id,
        label: q.label,
        avg: q.avg_score ?? null,
        count: q.response_count ?? q.count ?? null,
      }));
    }
    if (!heatmap?.questions) return [];
    return heatmap.questions.map(q => {
      let weighted = 0;
      let total    = 0;
      for (const row of heatmap.rows || []) {
        const cell = row.cells.find(c => c.question_id === q.id);
        if (cell && cell.avg_score != null && cell.count > 0) {
          weighted += cell.avg_score * cell.count;
          total    += cell.count;
        }
      }
      return {
        id: q.id,
        label: q.label,
        avg:   total ? weighted / total : null,
        count: total || null,
      };
    });
  }, [questions, heatmap]);

  const maxAvg = 5;

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          title="Responses"
          value={response_count ?? 0}
          subtitle={target_count ? `of ${target_count} invited` : 'collected'}
          color="blue"
          icon={UsersIcon}
        />
        <StatCard
          title="Target"
          value={target_count ?? '—'}
          subtitle="Eligible respondents"
          color="purple"
          icon={UserGroupIcon}
        />
        <StatCard
          title="Completion rate"
          value={completion_rate != null ? `${Math.round(completion_rate * 100)}%` : '—'}
          subtitle="Of target reached"
          color="green"
          icon={CheckBadgeIcon}
        />
        <StatCard
          title="Avg score"
          value={avg_score != null ? Number(avg_score).toFixed(2) : '—'}
          subtitle="Across rating questions"
          color="amber"
          icon={SparklesIcon}
        />
      </div>

      <div className="card p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="section-title">Overall sentiment</h3>
          <FaceSmileIcon className="w-5 h-5 text-gray-400" />
        </div>
        <div className="flex items-center gap-4">
          {sentimentChip(avg_sentiment)}
          <div className="flex-1 h-2 bg-gradient-to-r from-red-300 via-gray-200 to-green-400 rounded-full relative">
            {avg_sentiment != null && (
              <div
                className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-gray-700 rounded-full shadow"
                style={{ left: `${((Number(avg_sentiment) + 1) / 2) * 100}%`, transform: 'translate(-50%, -50%)' }}
              />
            )}
          </div>
          <div className="flex justify-between text-xs text-gray-400 w-20 ml-2">
            <span>−1</span>
            <span>+1</span>
          </div>
        </div>
      </div>

      <div className="card p-5">
        <h3 className="section-title mb-4">Question averages</h3>
        {questionAverages.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">
            No rating data yet.
          </p>
        ) : (
          <div className="space-y-3">
            {questionAverages.map(q => {
              const pct = q.avg != null ? (q.avg / maxAvg) * 100 : 0;
              return (
                <div key={q.id}>
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span className="truncate pr-3">{q.label}</span>
                    <span className="font-semibold text-gray-800 shrink-0">
                      {q.avg != null ? q.avg.toFixed(2) : '—'}
                      {q.count != null && <span className="text-gray-400 ml-1">({q.count})</span>}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={clsx(
                        'h-full transition-all',
                        q.avg >= 4.5 ? 'bg-green-500' :
                        q.avg >= 3.5 ? 'bg-green-400' :
                        q.avg >= 2.5 ? 'bg-amber-400' :
                                       'bg-red-400'
                      )}
                      style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

function HeatmapTab({ heatmap, isLoading }) {
  if (isLoading) {
    return <div className="flex justify-center p-12"><Spinner size="md" /></div>;
  }
  if (!heatmap || !heatmap.rows?.length || !heatmap.questions?.length) {
    return (
      <EmptyState
        title="No heatmap data"
        description="Responses are either insufficient or do not have a department breakdown available yet."
      />
    );
  }
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th className="th sticky left-0 bg-gray-50 z-10">Department</th>
            <th className="th text-center">Responses</th>
            {heatmap.questions.map(q => (
              <th key={q.id} className="th" title={q.label}>
                <span className="block max-w-[160px] truncate">{q.label}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {heatmap.rows.map(row => (
            <tr key={row.department} className="tr-hover">
              <td className="td sticky left-0 bg-white font-medium text-gray-800">
                {row.department}
              </td>
              <td className="td text-center text-sm text-gray-600">
                {row.response_count ?? '—'}
              </td>
              {heatmap.questions.map(q => {
                const cell = row.cells.find(c => c.question_id === q.id);
                const count = cell?.count ?? 0;
                const show  = cell && count >= 3 && cell.avg_score != null;
                return (
                  <td key={q.id} className="td">
                    <div
                      className={clsx(
                        'inline-flex items-center justify-center w-14 h-10 rounded-md text-sm font-semibold',
                        show ? scoreColor(cell.avg_score) : 'bg-gray-50 text-gray-300'
                      )}
                      title={show ? `${cell.avg_score.toFixed(2)} · n=${count}` : 'n<3 (hidden)'}
                    >
                      {show ? cell.avg_score.toFixed(1) : '—'}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center gap-3 px-4 py-3 border-t border-gray-100 text-xs text-gray-500 flex-wrap">
        <span>Scale:</span>
        <span className="inline-flex items-center gap-1"><span className="w-4 h-4 rounded bg-red-400" /> &lt;2.5</span>
        <span className="inline-flex items-center gap-1"><span className="w-4 h-4 rounded bg-amber-200" /> 2.5–3.5</span>
        <span className="inline-flex items-center gap-1"><span className="w-4 h-4 rounded bg-green-200" /> 3.5–4.5</span>
        <span className="inline-flex items-center gap-1"><span className="w-4 h-4 rounded bg-green-500" /> 4.5+</span>
        <span className="ml-auto text-gray-400">Cells with fewer than 3 responses are hidden for anonymity.</span>
      </div>
    </div>
  );
}

function RawResponsesTab({ responses, isAnonymous }) {
  if (!responses || responses.length === 0) {
    return (
      <EmptyState
        title="No responses yet"
        description="Individual responses will appear here as they are submitted."
      />
    );
  }
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th className="th">Respondent</th>
            <th className="th">Overall score</th>
            <th className="th">Sentiment</th>
            <th className="th">Submitted</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {responses.map((r, i) => (
            <tr key={r.id || i} className="tr-hover">
              <td className="td">
                <div className="flex items-center gap-2">
                  {isAnonymous ? (
                    <span className="w-7 h-7 bg-gray-100 text-gray-500 rounded-full flex items-center justify-center text-xs">
                      ?
                    </span>
                  ) : (
                    <span className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold">
                      {(r.respondent?.name || '?')[0]}
                    </span>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {isAnonymous ? 'Anonymous' : (r.respondent?.name || 'Unknown')}
                    </p>
                    {!isAnonymous && r.respondent?.department && (
                      <p className="text-xs text-gray-400">{r.respondent.department}</p>
                    )}
                  </div>
                </div>
              </td>
              <td className="td text-sm font-mono">
                {r.overall_score != null ? Number(r.overall_score).toFixed(2) : '—'}
              </td>
              <td className="td">{sentimentChip(r.sentiment_score)}</td>
              <td className="td text-sm text-gray-600">
                {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SurveyResults() {
  const { id } = useParams();
  const qc     = useQueryClient();
  const [tab, setTab]               = useState('overview');
  const [confirmClose, setConfirmClose] = useState(false);

  const { data: results, isLoading: loadingResults } = useQuery({
    queryKey: ['survey-results', id],
    queryFn:  () => surveyApi.results(id).then(r => r.data),
  });

  const { data: surveyData } = useQuery({
    queryKey: ['survey', id],
    queryFn:  () => surveyApi.get(id).then(r => r.data),
  });

  const { data: heatmap, isLoading: loadingHeatmap } = useQuery({
    queryKey: ['survey-heatmap', id],
    queryFn:  () => surveyApi.heatmap(id).then(r => r.data),
    enabled:  tab === 'heatmap' || tab === 'overview',
  });

  const closeMut = useMutation({
    mutationFn: () => surveyApi.update(id, { status: 'closed' }),
    onSuccess:  () => {
      toast.success('Survey closed');
      qc.invalidateQueries(['survey', id]);
      qc.invalidateQueries(['survey-results', id]);
      qc.invalidateQueries(['surveys']);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to close survey'),
  });

  if (loadingResults) {
    return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  }

  if (!results) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <EmptyState
          title="Results unavailable"
          description="This survey has no results yet or could not be loaded."
          action={<Link to="/app/surveys" className="btn-primary">Back to surveys</Link>}
        />
      </div>
    );
  }

  const survey      = results.survey || surveyData || {};
  const responses   = surveyData?.responses || results.responses || [];
  const isAnonymous = !!survey.is_anonymous;
  const isActive    = survey.status === 'active';

  const handleExport = () => {
    if (!responses.length) {
      toast.error('No responses to export');
      return;
    }
    const rows = responses.map(r => ({
      respondent:      isAnonymous ? 'Anonymous' : (r.respondent?.name || ''),
      department:      isAnonymous ? '' : (r.respondent?.department || ''),
      overall_score:   r.overall_score ?? '',
      sentiment_score: r.sentiment_score ?? '',
      submitted_at:    r.submitted_at || '',
    }));
    const safeName = (survey.name || 'survey').replace(/[^a-z0-9_-]+/gi, '_');
    downloadCsv(`${safeName}_responses.csv`, rows);
    toast.success('Export ready');
  };

  const tabs = [
    ['overview', 'Overview'],
    ['heatmap',  'Heatmap'],
    ['raw',      `Raw responses (${responses.length})`],
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <Link to="/app/surveys" className="text-sm text-blue-600 hover:underline">← Surveys</Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">{survey.name || 'Survey results'}</h1>
          <p className="text-sm text-gray-500 mt-1">
            <span className="capitalize">{survey.status || 'unknown'}</span>
            {survey.start_date && ` · ${new Date(survey.start_date).toLocaleDateString()}`}
            {survey.end_date && ` – ${new Date(survey.end_date).toLocaleDateString()}`}
            {isAnonymous && ' · Anonymous'}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExport} className="btn-secondary">
            <ArrowDownTrayIcon className="w-4 h-4" /> Export CSV
          </button>
          {isActive && (
            <button
              onClick={() => setConfirmClose(true)}
              className="btn-danger"
            >
              <LockClosedIcon className="w-4 h-4" /> Close survey
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={clsx(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab results={results} heatmap={heatmap} />}
      {tab === 'heatmap'  && <HeatmapTab heatmap={heatmap} isLoading={loadingHeatmap} />}
      {tab === 'raw'      && <RawResponsesTab responses={responses} isAnonymous={isAnonymous} />}

      <ConfirmModal
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        onConfirm={() => closeMut.mutate()}
        title="Close this survey?"
        message="No further responses will be accepted after closing. This action cannot be undone."
        danger
      />
    </div>
  );
}
